import React, {
    ReactElement,
    useState,
} from "react";
import { AiOutlinePlus } from "react-icons/ai";
import {
    FormType,
    useGetRequestTypesQuery,
} from "../../../../__generated__/api";
import { Loading } from "../../../../components";
import { Filters } from "../../../../components/filters/Filters";
import { NOBODY_GROUP_ID } from "../../../../components/pickers/user-groups/UserGroups";
import useArticleContext from "../../../../contexts/HelpContext";
import useRouting, { Link, useParams } from "../../../../contexts/Routing";
import useCurrentSession from "../../../../contexts/UserContext";
import { ErrorRemote } from "../../../error/Error";
import ActionFormSelected from "./ActionForm";
import usePortal from "../../../../contexts/PortalContext";

export function ActionFormList({
    type, title, singular, setting, description, className,
}: ActionFormListProps): ReactElement {
    const { data, error, loading: isLoading } = useGetRequestTypesQuery({
        fetchPolicy: "no-cache",
        variables: {
            type,
        },
    });

    const { Paths } = useRouting();
    const userSession = useCurrentSession();

    const [formFilters, setFormFilters] = useState<Set<string>>(() => new Set(userSession.user?.userGroups.toIdList("id")));
    const [formSearch, setFormSearch] = useState<string>("");

    if (isLoading) {
        return <Loading />;
    }
    if (error || !data) {
        return <ErrorRemote error={error} />;
    }

    let { forms } = data;

    if (formFilters.size > 0 && data.userGroups.isNotEmpty) {
        forms = forms?.filter(row => row.userGroupIds?.isEmpty
            || row.userGroupIds?.firstElement === NOBODY_GROUP_ID
            || row.userGroupIds?.some(id => formFilters.has(id)));
    }
    if (formSearch) {
        const search = formSearch.toLocaleLowerCase();
        forms = forms?.filter(row => row.name.toLocaleLowerCase().includes(search)
            || row.description?.toLocaleLowerCase().includes(search));
    }

    return (

        <div className={`border p-3 rounded bg-white ms-2 ${className}`}>
            {data.forms?.isNotEmpty ? (
                <>
                    <div className="d-flex justify-content-between align-items-center mb-3">
                        <h5 className="text-light mb-0">
                            {title}
                        </h5>
                        <Link
                            to={Paths.ADMINISTRATOR_SETTING_ID}
                            params={{
                                id: "new",
                                setting,
                            }}
                            className="btn btn-none btn-sm"
                        >
                            <AiOutlinePlus />
                            {" "}
                            {singular}
                        </Link>
                    </div>
                    <Filters
                        categories={data.userGroups.map(row => ({
                            label: row.name,
                            value: row.id,
                        }))}
                        selectedCategories={formFilters}
                        onSelected={setFormFilters}
                        search={formSearch}
                        onSetSearch={setFormSearch}
                    />
                    {forms.isNotEmpty ? (

                        <div className="list-group">
                            {forms?.map(field => (
                                <Link
                                    to={Paths.ADMINISTRATOR_SETTING_ID}
                                    key={field.id}
                                    params={{
                                        id: field.id,
                                        setting,
                                    }}
                                    className="list-group-item list-group-item-action flex-column align-items-start"
                                >
                                    <div className="d-flex w-100 align-items-end mb-2">
                                        <h5 className="mb-0">
                                            {field.name}
                                            {" "}
                                        </h5>

                                        <small className="ms-3 text-light " />
                                    </div>
                                    <small>{field.description}</small>
                                </Link>
                            ))}
                        </div>
                    ) : (
                        <div className="text-center text-light">
                            No
                            {" "}
                            {title.toLowerCase()}
                            {" "}
                            found matching your filters.
                        </div>
                    )}
                    <div className="d-flex justify-content-end align-items-center mt-3">

                        <Link
                            to={Paths.ADMINISTRATOR_SETTING_ID}
                            params={{
                                id: "new",
                                setting,
                            }}
                            className="btn btn-secondary btn-sm"
                        >
                            <AiOutlinePlus />
                            {" "}
                            {singular}
                        </Link>
                    </div>

                </>
            ) : (
                <div className="d-flex flex-column align-items-center justify-content-center h-100 text-light my-4">
                    <h5 className="text-light">
                        {title}
                    </h5>
                    <Link
                        to={Paths.ADMINISTRATOR_SETTING_ID}
                        params={{
                            id: "new",
                            setting,
                        }}
                        className="btn btn-secondary btn-sm my-3"
                    >
                        <AiOutlinePlus />
                        {" "}
                        Add
                        {" "}
                        {singular}
                    </Link>
                    <small>
                        {description}
                    </small>
                </div>

            )}

        </div>

    );
}

interface ActionFormListProps {
    type: FormType;
    title: string;
    singular: string;
    setting: string;
    description: string;
    className: string;
}

const FEATURE_WEBHOOKS = "frontend.webhooks.enabled";

export function ActionPage() {
    useArticleContext("urn:fq:article:7c8f62a03cbb41bc8a73612c329879f4");

    const { featureFlags } = usePortal();

    return (
        <div className="d-flex align-items-start">
            <ActionFormList
                className="col-6"
                title="Actions"
                singular="Action"
                type={FormType.Action}
                setting="workflow-actions"
                description={" Actions allow you to automate your workflow. Actions allow you to invoke a set of commands"
             + "manually from the Agent interface. Create an action, specify some fields, and kick of a set of"
             + "actions in a list directly from the Agent interface."}
            />

            {featureFlags.has(FEATURE_WEBHOOKS) && (
                <ActionFormList
                    className="col-6"
                    title="Webhooks"
                    singular="Webhook"
                    type={FormType.InboundWebhook}
                    setting="workflow-webhooks"
                    description={"Inbound webhooks allow you to receive data from outside services and "
                            + "integrations directly into Foqal Agent."}
                />
            )}

        </div>
    );
}
export default function ActionForm(): ReactElement {
    const { Paths } = useRouting();
    const { id } = useParams(Paths.ADMINISTRATOR_SETTING_ID);
    return id ? <ActionFormSelected id={id} /> : <ActionPage />;
}
